import { cx } from "@jugl-web/utils";
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";
import { FC, useMemo } from "react";
import { ReactComponent as ChevronDownIcon } from "./assets/chevron-down.svg";

interface CountryOption {
  iso: CountryCode;
  code: string;
  name: string;
}

const buildCountryOptionLabel = (country: CountryOption) =>
  `${country.code} ${country.name}`;

export interface NativeCountryCodeSelectProps {
  value: CountryCode;
  suggestedCountryCode: CountryCode;
  className?: string;
  onChange: (country: CountryCode) => void;
}

export const NativeCountryCodeSelect: FC<NativeCountryCodeSelectProps> = ({
  value,
  suggestedCountryCode,
  className,
  onChange,
}) => {
  const countryOptions = useMemo<CountryOption[]>(() => {
    const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

    const countries = getCountries()
      .map((iso) => ({
        iso,
        code: `+${getCountryCallingCode(iso)}`,
        name: regionNames.of(iso) ?? iso,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return countries;
  }, []);

  const suggestedCountryOption = useMemo<CountryOption>(
    () =>
      countryOptions.find((country) => country.iso === suggestedCountryCode) ??
      countryOptions[0],
    [countryOptions, suggestedCountryCode]
  );

  const selectedCountry = useMemo(
    () =>
      countryOptions.find((country) => country.iso === value) ??
      countryOptions[0],
    [countryOptions, value]
  );

  return (
    <div className={cx("relative inline-flex items-center", className)}>
      <span
        aria-hidden
        className="pointer-events-none font-secondary text-sm font-normal text-dark select-none"
      >
        {selectedCountry.code}
      </span>
      <ChevronDownIcon className="ml-1.5 shrink-0" />
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as CountryCode)}
        aria-label="Country code"
        className="absolute inset-0 cursor-pointer border-0 bg-transparent opacity-0 outline-none"
      >
        <optgroup label="Suggested">
          <option
            key={`${suggestedCountryCode}-suggested`}
            value={suggestedCountryCode}
          >
            {buildCountryOptionLabel(suggestedCountryOption)}
          </option>
        </optgroup>
        <optgroup label="All countries">
          {countryOptions.map((countryOption) => (
            <option key={countryOption.iso} value={countryOption.iso}>
              {buildCountryOptionLabel(countryOption)}
            </option>
          ))}
        </optgroup>
      </select>
    </div>
  );
};
